// src/components/dashboard/tiles/TradingBlockTile.js
import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Plus, X } from 'lucide-react';
import { dynastyTheme } from '../../../services/colorService';
import { leaguesAPI } from '../../../services/apiService';
import { usePlayerModal } from '../../../contexts/PlayerModalContext';
import PlayerLink from '../../PlayerLink';

const SCROLLBAR_CSS = `
.dd-scroll::-webkit-scrollbar { width: 3px; }
.dd-scroll::-webkit-scrollbar-track { background: transparent; }
.dd-scroll::-webkit-scrollbar-thumb { background: rgba(234,179,8,0.4); border-radius: 99px; }
.dd-scroll::-webkit-scrollbar-thumb:hover { background: rgba(234,179,8,0.7); }
`;

const TEAM_PALETTE = ['#60a5fa','#f472b6','#fb923c','#a78bfa','#34d399','#fbbf24','#f87171','#38bdf8'];

const DOT_COLOR = {
  free_agent: '#34d399',
  waiver_claim:     '#34d399',
  release:          '#f87171',
  trade:            '#c084fc',
  roster_move:      '#60a5fa',
};

const TYPE_LABEL = {
  free_agent: 'FA Claim',
  waiver_claim:     'Waiver',
  release:          'Release',
  trade:            'Trade',
  roster_move:      'Move',
};

function fmtDate(s) {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function getTxTeam(t) {
  return t.transaction_type === 'release' ? t.from_team_name : t.to_team_name;
}

// Waiver awards are written transaction_type='fa_acquisition' (and FA adds also
// arrive as 'fa_add' / 'free_agent' / 'free_agent_claim') with the real method in
// details.acquisition_method (Bible 22 allowed type/method disagreement). Collapse
// the FA family to one effective type, and surface waivers distinctly via
// acquisition_method so they label as "Waiver" not "FA Claim".
const FA_TYPES = ['free_agent', 'free_agent_claim', 'fa_acquisition', 'fa_add'];
function effectiveType(t) {
  if (FA_TYPES.includes(t.transaction_type)) {
    return t.acquisition_method === 'waiver_claim' ? 'waiver_claim' : 'free_agent';
  }
  return t.transaction_type;
}

function getTxPrice(t) {
  const sal = t.salary != null ? `$${t.salary}` : null;
  const years = t.contract_years;   // serialized contract LENGTH (no fallback to another field)
  const eff = effectiveType(t);
  if (eff === 'free_agent' || eff === 'waiver_claim') {
    if (!sal) return '—';
    return years ? `${sal} / ${years} yr` : sal;
  }
  // Release salary is intentionally NOT shown: a released player's salary is spent
  // (sunk), not returned to the team, so a dollar figure reads as a refund.
  if (eff === 'release') return '—';
  if (eff === 'trade')   return sal || '—';
  return '—';
}

const TH = { fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' };
const COL = {
  pos:      { width: 36, flexShrink: 0, fontSize: 11, fontWeight: 700, color: '#9ca3af', textAlign: 'center' },
  team:     { width: 100, flexShrink: 0 },
  contract: { width: 80, flexShrink: 0, textAlign: 'right' },
  price:    { width: 80, flexShrink: 0, textAlign: 'right' },
  date:     { width: 52, flexShrink: 0, textAlign: 'right' },
  remove:   { width: 20, flexShrink: 0 },
};

const SELECT_STYLE = {
  background: '#1f2937',
  border: '1px solid #374151',
  borderRadius: 4,
  color: '#9ca3af',
  fontSize: 11,
  padding: '2px 6px',
  cursor: 'pointer',
  maxWidth: 130,
};

function TeamChip({ name, colorMap }) {
  const color = colorMap[name] || '#78716c';
  return (
    <span style={{
      background: color + '22',
      color,
      border: `1px solid ${color}55`,
      borderRadius: 4,
      padding: '1px 5px',
      fontSize: 10,
      fontWeight: 600,
      display: 'block',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      maxWidth: 100,
    }}>
      {name || '—'}
    </span>
  );
}

const ROW_H = 40;

const TradingBlockTile = ({ leagueId, league, currentUserTeamId, teams }) => {
  // Block state
  const [blockItems, setBlockItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [addTab, setAddTab] = useState('player');
  const [myRoster, setMyRoster] = useState([]);
  const [myPicks, setMyPicks] = useState([]);
  const [selectedPlayerId, setSelectedPlayerId] = useState('');
  const [selectedPickId, setSelectedPickId] = useState('');
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Transaction state
  const [transactions, setTransactions] = useState([]);
  const [loadingTx, setLoadingTx] = useState(true);

  // Filter state
  const [dayFilter, setDayFilter] = useState('30d');
  const [blockTeam, setBlockTeam] = useState('All Teams');
  const [actTeam, setActTeam] = useState('All Teams');

  const { openPlayerModal } = usePlayerModal();

  const isMyItem = (teamId) => teamId === currentUserTeamId;

  // Fetch trading block
  const load = async () => {
    setLoading(true);
    try {
      const result = await leaguesAPI.getTradingBlock(leagueId);
      setBlockItems(result?.players || []);
    } catch (e) {
      setBlockItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch transactions (365-day window, filter client-side)
  useEffect(() => {
    const loadTx = async () => {
      setLoadingTx(true);
      try {
        // Fetch a wide window/limit so the client-side "All" filter is honest and the
        // 7d/30d windows narrow from the full set (not a pre-capped 50 rows / 365 days).
        const result = await leaguesAPI.getTransactions(leagueId, { days_back: 3650, limit: 200 });
        setTransactions(result.transactions || []);
      } catch (e) {
        console.error('Failed to load transactions:', e);
        setTransactions([]);
      } finally {
        setLoadingTx(false);
      }
    };
    if (leagueId) loadTx();
  }, [leagueId]);

  useEffect(() => { if (leagueId) load(); }, [leagueId]);

  useEffect(() => {
    if (showAddModal && currentUserTeamId) loadMyAssets();
  }, [showAddModal]);

  const loadMyAssets = async () => {
    try {
      const [rosterResult, picksResult] = await Promise.all([
        leaguesAPI.getTeamRosterCanonical(leagueId, currentUserTeamId),
        leaguesAPI.getTeamDraftPicks(leagueId, currentUserTeamId, true) // §4.5.24 tradeable only
      ]);
      console.log('ROSTER:', rosterResult);
      console.log('PICKS:', picksResult);
      const onBlockPlayerIds = blockItems
        .filter(i => i.item_type === 'player')
        .map(i => i.league_player_id);
      const onBlockPickIds = blockItems
        .filter(i => i.item_type !== 'player')
        .map(i => i.pick_id);
      // The /teams/{team_id}/roster endpoint intentionally returns BOTH current
      // roster AND former players with accrued stats (canonical full-attribution
      // view per `player_service.build_roster_response`). Former players carry
      // `is_historical: true` NESTED at `player.info.is_historical` (set in
      // `accrued_stats_repository.py:994` inside the `info` jsonb_build_object).
      // The Trading Block dropdown's contract is "current roster only" — filter
      // them out here (consumer-side discrimination per Bible §14.layered: the
      // canonical endpoint serves a richer shape; the consumer scopes).
      // CANONICAL FIELD PATH: p.info?.is_historical (NESTED). NOT top-level
      // p.is_historical (the prior fix used the wrong path and was a no-op).
      setMyRoster(rosterResult.players.filter(p => !p.info?.is_historical && !onBlockPlayerIds.includes(p.ids.league_player)));
      setMyPicks(picksResult.picks.filter(p => !onBlockPickIds.includes(p.pick_id)));
    } catch (e) {
      setMyRoster([]);
      setMyPicks([]);
    }
  };

  const handleAdd = async () => {
    setSaving(true);
    setError('');
    try {
      if (addTab === 'player') {
        if (!selectedPlayerId) throw new Error('Select a player');
        await leaguesAPI.addToTradingBlock(leagueId, {
          item_type: 'player',
          league_player_id: selectedPlayerId,
          note: comment
        });
      } else {
        if (!selectedPickId) throw new Error('Select a pick');
        await leaguesAPI.addToTradingBlock(leagueId, {
          item_type: 'pick',
          pick_id: selectedPickId,
          pick_type: 'regular',
          note: comment
        });
      }
      setShowAddModal(false);
      setSelectedPlayerId('');
      setSelectedPickId('');
      setComment('');
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Failed to add');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (blockId) => {
    try {
      await leaguesAPI.removeFromTradingBlock(leagueId, blockId);
      await load();
    } catch (e) {}
  };

  const resetModal = () => {
    setShowAddModal(false);
    setSelectedPlayerId('');
    setSelectedPickId('');
    setComment('');
    setError('');
    setAddTab('player');
  };

  const getPickLabel = (item) => {
    const prefix = item.item_type === 'ml_pick' ? 'ML ' : '';
    const year = item.pick_year || '?';
    const round = item.pick_round || '?';
    const orig = item.pick_original_owner ? ` (${item.pick_original_owner}'s)` : '';
    return `${year} ${prefix}R${round}${orig}`;
  };

  // Team color map — derived from all names seen across both panels
  const teamColorMap = useMemo(() => {
    const allNames = [...new Set([
      ...blockItems.map(p => p.team_name),
      ...transactions.map(t => t.to_team_name),
      ...transactions.map(t => t.from_team_name),
    ].filter(n => n && n !== 'Free Agency'))];
    const map = { 'Free Agency': '#78716c' };
    allNames.forEach((name, i) => { map[name] = TEAM_PALETTE[i % TEAM_PALETTE.length]; });
    return map;
  }, [blockItems, transactions]);

  // Block dropdown options
  const blockTeams = ['All Teams', ...(teams || []).map(t => t.team_name).filter(Boolean)];

  // Transaction dropdown options
  const actTeamNames = ['All Teams', ...(teams || []).map(t => t.team_name).filter(Boolean)];

  // Filtered block
  const filteredBlock = blockTeam === 'All Teams'
    ? blockItems
    : blockItems.filter(p => p.team_name === blockTeam);

  // Filtered transactions — client-side date window (rolling, relative to now) + team.
  // 'All' applies no date filter; 7d/30d keep rows within that many days of today.
  const dayWindow = dayFilter === '7d' ? 7 : dayFilter === '30d' ? 30 : null;
  const cutoff = dayWindow == null ? null : new Date(Date.now() - dayWindow * 24 * 60 * 60 * 1000);

  const filteredTx = transactions.filter(t => {
    const dateOk = !cutoff || new Date(t.transaction_date) >= cutoff;
    const teamName = getTxTeam(t);   // release -> from_team, else to_team (fixes the 'drop' check)
    const teamOk = actTeam === 'All Teams' || teamName === actTeam;
    return dateOk && teamOk;
  });

  return (
    <div
      className={dynastyTheme.components.card.base}
      style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column', height: 230 }}
    >
      <style>{SCROLLBAR_CSS}</style>

      {/* Header row */}
      <div style={{ display: 'flex', height: 44, borderBottom: '1px solid #374151', flexShrink: 0 }}>
        {/* Left header */}
        <div style={{ flex: 1, borderRight: '1px solid #374151', display: 'flex', alignItems: 'center', gap: 8, padding: '0 14px' }}>
          <h3 className={`text-lg font-bold ${dynastyTheme.classes.text.white}`}>Trading Block</h3>
          {currentUserTeamId && (
            <button
              onClick={() => setShowAddModal(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, padding: '2px 8px', borderRadius: 4, border: '1px solid rgba(234,179,8,0.3)', color: '#eab308', background: 'transparent', cursor: 'pointer', flexShrink: 0 }}
            >
              <Plus size={11} /> Add
            </button>
          )}
          <div style={{ flex: 1 }} />
          <select value={blockTeam} onChange={e => setBlockTeam(e.target.value)} style={SELECT_STYLE}>
            {blockTeams.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        {/* Right header */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, padding: '0 14px' }}>
          <h3 className={`text-lg font-bold ${dynastyTheme.classes.text.white}`}>Transactions</h3>
          <div style={{ flex: 1 }} />
          <div style={{ display: 'flex', gap: 3 }}>
            {['7d', '30d', 'All'].map(f => (
              <button
                key={f}
                onClick={() => setDayFilter(f)}
                style={{
                  fontSize: 11, padding: '2px 7px', borderRadius: 4, border: '1px solid',
                  borderColor: dayFilter === f ? 'rgba(234,179,8,0.5)' : '#374151',
                  background: dayFilter === f ? 'rgba(234,179,8,0.1)' : 'transparent',
                  color: dayFilter === f ? '#eab308' : '#6b7280',
                  cursor: 'pointer', fontWeight: 600,
                }}
              >{f}</button>
            ))}
          </div>
          <select value={actTeam} onChange={e => setActTeam(e.target.value)} style={SELECT_STYLE}>
            {actTeamNames.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>

      {/* Column headers */}
      <div style={{ display: 'flex', borderBottom: '1px solid #1f2937', flexShrink: 0 }}>
        <div style={{ flex: 1, borderRight: '1px solid #374151', display: 'flex', alignItems: 'center', padding: '4px 14px', gap: 6 }}>
          <span style={{ ...TH, ...COL.pos }}>Pos</span>
          <span style={{ ...TH, flex: 1 }}>Player</span>
          <span style={{ ...TH, ...COL.team }}>Team</span>
          <span style={{ ...TH, ...COL.contract }}>Contract</span>
          <span style={{ ...COL.remove }} />
        </div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', padding: '4px 14px', gap: 6 }}>
          <span style={{ ...TH, ...COL.pos }}>Pos</span>
          <span style={{ ...TH, flex: 1 }}>Player</span>
          <span style={{ ...TH, ...COL.team }}>Team</span>
          <span style={{ ...TH, ...COL.price, textAlign: 'right' }}>Price</span>
          <span style={{ ...TH, ...COL.date, textAlign: 'right' }}>Date</span>
        </div>
      </div>

      {/* Data rows */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* Left — trading block */}
        <div className="dd-scroll" style={{ flex: 1, borderRight: '1px solid #374151', overflowY: 'auto' }}>
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 16 }}>
              <div className="w-4 h-4 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
              <span style={{ fontSize: 13, color: '#9ca3af' }}>Loading...</span>
            </div>
          ) : filteredBlock.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center' }}>
              <p style={{ fontSize: 13, color: '#6b7280' }}>No players or picks on the block.</p>
              <p style={{ fontSize: 11, color: '#4b5563', marginTop: 4 }}>Add assets you're willing to trade.</p>
            </div>
          ) : (
            filteredBlock.map((item, i) => {
              const isPlayer = item.item_type === 'player';
              const pos = isPlayer ? item.position : (item.item_type === 'ml_pick' ? 'ML' : 'PICK');
              const name = isPlayer ? item.player_name : getPickLabel(item);
              const mine = isMyItem(item.team_id);
              return (
                <div
                  key={i}
                  title={item.note || undefined}
                  style={{ display: 'flex', alignItems: 'flex-start', gap: 6, padding: '0 14px', height: ROW_H, borderBottom: '1px solid #111827' }}
                >
                  <span style={{ ...COL.pos }}>{pos}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#f9fafb', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <PlayerLink mlbId={item.mlb_id} name={name} style={{ fontSize: 13, fontWeight: 600, color: '#f9fafb' }} />
                      {item.note ? <span style={{ color: '#a8a29e', fontStyle: 'italic', fontWeight: 400, fontSize: 13 }}> — {item.note}</span> : null}
                    </div>
                    {isPlayer && item.info?.mlb_team && (
                      <div style={{ fontSize: 11, color: '#6b7280' }}>{item.info?.mlb_team}</div>
                    )}
                  </div>
                  <div style={{ ...COL.team }}>
                    <TeamChip name={item.team_name} colorMap={teamColorMap} />
                  </div>
                  <div style={{ ...COL.contract }}>
                    {isPlayer && (() => {
                      const salary = item.salary ?? item.contract_salary ?? item.league_context?.financial?.contract_salary;
                      const years  = item.contract_years ?? item.league_context?.financial?.contract_years;
                      const ct     = item.contract_type  ?? item.league_context?.financial?.contract_type;
                      return salary != null ? (
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#eab308', textAlign: 'right' }}>
                          ${salary} / {ct || '—'}
                        </div>
                      ) : null;
                    })()}
                  </div>
                  <div style={{ ...COL.remove, display: 'flex', justifyContent: 'center' }}>
                    {mine && (
                      <button
                        onClick={() => handleRemove(item.block_id)}
                        title="Remove from block"
                        style={{ color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 1 }}
                        onMouseEnter={e => e.currentTarget.style.color = '#f87171'}
                        onMouseLeave={e => e.currentTarget.style.color = '#6b7280'}
                      >
                        <X size={13} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Right — transactions */}
        <div className="dd-scroll" style={{ flex: 1, overflowY: 'auto' }}>
          {loadingTx ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 16 }}>
              <div className="w-4 h-4 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
              <span style={{ fontSize: 13, color: '#9ca3af' }}>Loading...</span>
            </div>
          ) : filteredTx.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center' }}>
              <p style={{ fontSize: 13, color: '#6b7280' }}>No transactions in this window.</p>
            </div>
          ) : (
            filteredTx.map((t, i) => {
              const eff = effectiveType(t);
              const dotColor = DOT_COLOR[eff] || '#6b7280';
              const typeLabel = TYPE_LABEL[eff] || eff;
              const teamName = getTxTeam(t);
              const price = getTxPrice(t);
              // Trades and roster moves carry their detail in the serialized notes:
              // trades = the per-team legs (built by transaction_service._build_side_summary),
              // moves = the slot/status transition (e.g. "Moved from bench to active").
              // The PRICE column is "—" for both, so surface the detail under the name.
              // Single-lined here, full text on hover.
              const detail = (eff === 'trade' || eff === 'roster_move') && t.notes
                ? t.notes.replace(/\s*\n\s*/g, ' · ')
                : null;
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '0 14px', height: ROW_H, borderBottom: '1px solid #111827' }}>
                  <span style={{ ...COL.pos }}>{t.position || '—'}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: dotColor, flexShrink: 0, display: 'inline-block' }} />
                      <PlayerLink mlbId={t.player_id} name={t.player_name} style={{ fontSize: 13, fontWeight: 600, color: '#f9fafb' }} />
                    </div>
                    <div style={{ display: 'flex', gap: 5, alignItems: 'baseline', marginTop: 1, paddingLeft: 11, minWidth: 0 }}>
                      <span style={{ fontSize: 10, color: dotColor, fontWeight: 600, flexShrink: 0 }}>
                        {typeLabel}
                      </span>
                      {detail && (
                        <span
                          title={t.notes}
                          style={{ fontSize: 10, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}
                        >
                          {detail}
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ ...COL.team }}>
                    <TeamChip name={teamName} colorMap={teamColorMap} />
                  </div>
                  <div style={{ ...COL.price, fontSize: 12, fontWeight: 600, color: '#eab308' }}>{price}</div>
                  <div style={{ ...COL.date, fontSize: 11, color: '#6b7280' }}>{fmtDate(t.transaction_date)}</div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Add Modal — unchanged */}
      {showAddModal && createPortal(
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center" style={{ zIndex: 9999 }}>
          <div className={`${dynastyTheme.components.card.base} w-full max-w-md mx-4`}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-bold ${dynastyTheme.classes.text.white}`}>
                  Add to Trading Block
                </h3>
                <button onClick={resetModal} className={`${dynastyTheme.classes.text.neutralLight} hover:text-white`}>
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex rounded-lg p-1 mb-4 bg-neutral-800">
                {['player', 'pick'].map(tab => (
                  <button
                    key={tab}
                    onClick={() => setAddTab(tab)}
                    className={`flex-1 text-sm py-1.5 rounded-md font-medium transition-colors ${
                      addTab === tab
                        ? `bg-yellow-500/20 ${dynastyTheme.classes.text.primary}`
                        : dynastyTheme.classes.text.neutralLight
                    }`}
                  >
                    {tab === 'player' ? 'Player' : 'Draft Pick'}
                  </button>
                ))}
              </div>

              {error && (
                <div className="mb-4 p-3 rounded bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                  {error}
                </div>
              )}

              {addTab === 'player' ? (
                <>
                  <label className={`${dynastyTheme.components.label} block mb-2`}>Select Player</label>
                  <select
                    value={selectedPlayerId}
                    onChange={e => setSelectedPlayerId(e.target.value)}
                    className={`${dynastyTheme.components.input} w-full mb-4`}
                  >
                    <option value="">— Choose a player —</option>
                    {myRoster.map(p => (
                      <option key={p.ids.league_player} value={p.ids.league_player}>
                        {p.info?.full_name} ({p.info?.position}) · ${p.league_context?.financial?.contract_salary ?? 0}
                      </option>
                    ))}
                  </select>
                </>
              ) : (
                <>
                  <label className={`${dynastyTheme.components.label} block mb-2`}>Select Pick</label>
                  <select
                    value={selectedPickId}
                    onChange={e => setSelectedPickId(e.target.value)}
                    className={`${dynastyTheme.components.input} w-full mb-4`}
                  >
                    <option value="">— Choose a pick —</option>
                    {myPicks.map(p => (
                      <option key={p.pick_id} value={p.pick_id}>
                        {p.year} Round {p.round}
                      </option>
                    ))}
                  </select>
                </>
              )}

              <label className={`${dynastyTheme.components.label} block mb-2`}>
                Comment <span className={dynastyTheme.classes.text.neutralDark}>(optional)</span>
              </label>
              <textarea
                value={comment}
                onChange={e => setComment(e.target.value)}
                placeholder="e.g. Looking for starting pitching or outfield depth..."
                className={`${dynastyTheme.components.input} w-full mb-1 resize-none`}
                rows={3}
                maxLength={300}
              />
              <div className={`text-xs ${dynastyTheme.classes.text.neutralDark} text-right mb-4`}>
                {comment.length}/300
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleAdd}
                  disabled={(!selectedPlayerId && addTab === 'player') || (!selectedPickId && addTab === 'pick') || saving}
                  className={dynastyTheme.utils.getComponent('button', 'primary', 'md')}
                >
                  {saving ? 'Adding...' : 'Add to Block'}
                </button>
                <button
                  onClick={resetModal}
                  className={dynastyTheme.utils.getComponent('button', 'secondary', 'md')}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default TradingBlockTile;
